<template>
  <div
    :class="[
      'column position-relative',
      colType,
      {
        question: cell.isQuestion,
        answer: cell.isAnswer,
        placeholder: isLetterPlaceholder,
        active: cell.active,
        warning: hasQuestion,
        danger: isResetQuestion,
        fixed: cell.fixed,
      },
    ]"
    :style="{
      height: `${cell.height}%`,
      'background-color': cell.isQuestion ? cell.settings.background : null,
    }"
    v-if="!cell.hide"
    @click.stop="onClick(cell, $event)"
    @dblclick.stop="onDblClick(cell)"
    :data-positiony="cell.positiony"
    :data-positionx="cell.positionx"
    :data-question="hasQuestion"
  >
    <template v-if="showQuestionTemplate">
      <svg
        class="question-svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
      >
        <path
          d="M10 6h44a6 6 0 016 6v28a6 6 0 01-6 6H26L14 56v-10h-4a6 6 0 01-6-6V12a6 6 0 016-6z"
          fill="none"
          stroke="currentColor"
          stroke-width="4"
          stroke-linejoin="round"
        />
      </svg>
    </template>
    <template v-else-if="cell?.children?.length > 0 && showText">
      <puzzle-cell
        v-for="c_cell in cell.children"
        :key="c_cell.positiony + c_cell.positionx"
        :cell="c_cell"
      />
    </template>
    <template v-else>
      <!-- <div class="number" v-if="showNumbers">{{ cell.positiony }}{{ cell.positionx }}</div> -->
      <input
        ref="cellInput"
        autocomplete="off"
        type="text"
        maxlength="1"
        class="cell-input"
        v-if="showLetterInput"
        v-model="cell.text"
        :readonly="readOnly"
        :disabled="readOnly"
        @focus="$event.target.select()"
        @keyup="changeLetter(cell)"
        @blur="editing = false"
      />
      <p
        v-else-if="showText"
        :style="{
          fontWeight: 'bold',
          fontSize: !cell.isAnswer ? `${cell.settings?.font_size}px` : null,
        }"
      >
        {{ cell.text }}
      </p>
    </template>
    <div
      v-if="cell.arrow_position && cell.arrow_position.slug && showText"
      :class="['arrow', `position_${cell.arrow_position.slug}`]"
    >
      <div
        v-if="cell.arrow_direction && cell.arrow_direction.slug"
        :class="['arrow', `direction_${cell.arrow_direction.slug}`]"
      ></div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import {
  CELL,
  _PUZZLE_GROUP_OPTION_,
  _PUZZLE_SELECTED_CELL_,
  cellHasLinkedAnswer,
  createResettableCellFields,
  letterIsBound,
} from './PuzzleModel';

export default {
  name: 'puzzle-cell',
  props: {
    cell: {
      type: Object,
      require: true,
    },
    templateMode: {
      type: Boolean,
      default: false,
    },
    isPlanningMode: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editing: false,
    };
  },
  computed: {
    isLetterPlaceholder() {
      return this.cell.isAnswer && !letterIsBound(this.cell);
    },
    showLetterInput() {
      return (
        this.cell.isAnswer &&
        this.showText &&
        (this.editing || !letterIsBound(this.cell))
      );
    },
    hasQuestion() {
      if (
        this.cell.answer &&
        this.cell.question &&
        cellHasLinkedAnswer(this.cell) &&
        (this.cell.text === '' || this.cell.text === null)
      ) {
        return true;
      }
      return false;
    },
    isResetQuestion() {
      const { cell } = this;
      const children = cell.children || [];
      const isQuestionCell = !cell.isAnswer && cell.id !== null;
      const hasEmptyText = cell.text === '' || cell.text === null;
      return (
        !this.hasQuestion &&
        isQuestionCell &&
        hasEmptyText &&
        cell.direction !== null &&
        children.length === 0
      );
    },
    showQuestionTemplate() {
      if (this.cell.isQuestion && this.templateMode) return true;

      return this.cell.isQuestion && this.isPlanningMode && !this.hasQuestion;
    },
    showNumbers() {
      if (
        process.env.NODE_ENV === 'production' ||
        this.$store.state.puzzle.print
      ) {
        return false;
      }
      return true;
    },
    selectedCell() {
      const { puzzleCell } = this.$store.state.puzzle;
      if (puzzleCell) {
        return puzzleCell;
      }
      return cloneDeep(CELL);
    },
    table() {
      return this.$store.state.puzzle.puzzle.table;
    },
    colType() {
      const types = {
        col: 'column',
        row: 'line',
        colspan: 'colspan',
        rowspan: 'rowspan',
        row_rowspan: 'rowspan line',
        colspan_row: 'colspan line',
        colspan_col: 'colspan_col',
      };
      return types[this.cell.type];
    },
    showText() {
      if (this.$store.state.puzzle.print) {
        if (this.$store.state.puzzle.printAnswer) {
          if (this.cell.isAnswer) {
            return true;
          }
          return false;
        }
        if (this.$store.state.puzzle.printQuestion) {
          if (!this.cell.isAnswer) {
            return true;
          }
          return false;
        }
      }
      return true;
    },
  },
  methods: {
    onClick(cell, event) {
      if (this.readOnly) return;

      if (this.canBeEdited(cell) && !this.editing) {
        this.editing = true;

        this.$nextTick(() => {
          if (this.$refs.cellInput) {
            this.$refs.cellInput.focus();
            this.$refs.cellInput.select();
          }
        });

        return;
      }

      if (!this.templateMode && !this.isPlanningMode && !cell.isAnswer) {
        this.selectCell(cell, event);
      }
    },
    onDblClick(cell) {
      if (this.readOnly) return;

      if (cell.isAnswer) {
        this.toggleFixed(cell);
        return;
      }

      if (this.templateMode || this.isPlanningMode) {
        this.toggleQuestion(cell);
      }
    },
    toggleFixed(cell) {
      if (this.readOnly) return;

      cell.fixed = !cell.fixed;
      cell.updated = true;
      this.$forceUpdate();
    },
    selectCell(cell, event) {
      if (this.readOnly) return cell;

      cell.active = !cell.active;
      if (this.selectedCell.active) {
        Events.$emit(_PUZZLE_GROUP_OPTION_, cell);
        return cell;
      }

      const el = event && event.currentTarget ? event.currentTarget : null;
      const container = el ? el.closest('.puzzle-container') : null;
      let cellRect = null;
      if (el && container) {
        const domRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        cellRect = {
          top: domRect.top - containerRect.top + container.scrollTop,
          left: domRect.left - containerRect.left + container.scrollLeft,
          width: domRect.width,
          height: domRect.height,
          containerWidth: containerRect.width,
        };
      }
      Events.$emit(_PUZZLE_SELECTED_CELL_, cell, cellRect);
      return cell;
    },
    toggleQuestion(cell) {
      if (this.readOnly) return;

      cell.isQuestion = !cell.isQuestion;
      cell.updated = true;
      this.$forceUpdate();
    },
    changeLetter(cell) {
      if (this.readOnly) {
        return;
      }

      cell.text = cell.text ? cell.text.toUpperCase() : '';

      if (this.canBeEdited(cell) && this.editing) {
        this.resolveUpdatedCell(cell);
      }
    },
    resolveUpdatedCell(cell) {
      if (!this.canResolveUpdatedCell(cell)) return;

      cell.related_questions.forEach(ref => {
        const questionCell = this.table.find(c => c.id === ref.id);
        if (!questionCell) return;

        const target = this.resolveTargetCell(questionCell, ref);
        this.resetCell(target);
      });

      this.$forceUpdate();
    },
    resolveTargetCell(cell, questionRef) {
      if (!questionRef.child || !cell.children?.length) {
        return cell;
      }

      return (
        cell.children.find(child => child.id === questionRef.child) || cell
      );
    },
    canResolveUpdatedCell(cell) {
      return Boolean(cell?.related_questions?.length && this.table);
    },
    resetCell(cell) {
      Object.assign(cell, createResettableCellFields());
    },
    canBeEdited(cell) {
      return !!(cell.isAnswer && cell.related_questions && this.table);
    },
    reRender() {
      this.$forceUpdate();
    },
  },
  mounted() {},
};
</script>

<style lang="scss" scoped>
.number {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 10px;
  font-weight: normal;
}
.cell-input {
  border: 0;
  width: 100%;
  height: 100%;
  text-align: center;
  color: #bbb;
  font-weight: bold;
}

.question-svg {
  color: #000;
}

.fixed .cell-input {
  color: #fff;
  background: #bbb;
}
</style>
